import { AgentRun } from "@/models/agent/agentRun";
import { Command, CommandDetail } from "@/models/agent/command";
import { AgentMessage } from "@/models/agent/message";
import { SubAgent } from "@/models/agent/subAgent";
import { Thread } from "@/models/agent/thread";
import {
  AgentExecuteRequest,
  AgentExecuteResponse,
  AgentRunResponse,
  CancelAgentRequest,
  CancelAgentResponse,
  CommandDetailSchema,
  CommandInfo,
  CommandsResponse,
  CreateCommandRequest,
  CreateSubAgentRequest,
  DeleteCommandResponse,
  DeleteSandboxFileResponse,
  DeleteSubAgentResponse,
  GetAgentRunsResponse,
  ListSandboxFilesResponse,
  MessageSchema,
  MessagesResponse,
  SubAgentDetailSchema,
  SubAgentInfo,
  SubAgentsResponse,
  ThreadCostsDto,
  ThreadSchema,
  ThreadsResponse,
  UpdateCommandRequest,
  UpdateSubAgentRequest,
  UploadSandboxFileResponse,
} from "@/services/agent/agent.api.types";
import AgentEndpoints from "@/services/agent/agent.endpoint";
import ApiService from "@/services/api/api.service";

export class AgentService {
  /**
   * スレッド一覧を取得
   */
  static async getThreads(
    workspaceId: string,
    offset: number,
    limit: number,
  ): Promise<Thread[]> {
    try {
      const response = await ApiService.fetchData<undefined, ThreadsResponse>({
        url: AgentEndpoints.GET_THREADS(offset, limit, workspaceId),
        method: "GET",
      });

      return response.data.threads.map(buildThread);
    } catch (error) {
      console.error("Failed to fetch threads:", error);
      throw error;
    }
  }

  /**
   * 特定スレッドのメッセージ一覧を取得
   */
  static async getThreadMessages(threadId: string, offset: number, limit = 40): Promise<{ data: AgentMessage[]; }> {
    const url = AgentEndpoints.GET_THREAD_MESSAGES(threadId, offset, limit);
    const response = await ApiService.fetchData<undefined, MessagesResponse>({
      url,
      method: "GET",
    });

    return {
      data: response.data.messages.map(buildMessage),
    };
  }

  /**
   * エージェント実行開始
   */
  static async executeAgent(request: AgentExecuteRequest): Promise<AgentExecuteResponse> {
    const result = await ApiService.fetchData<AgentExecuteRequest, AgentExecuteResponse>({
      url: AgentEndpoints.EXECUTE_AGENT(),
      method: "POST",
      data: request,
    });

    return result.data;
  }

  /**
   * エージェント実行キャンセル
   */
  static async cancelAgent(request: CancelAgentRequest): Promise<void> {
    const result = await ApiService.fetchData<CancelAgentRequest, CancelAgentResponse>({
      url: AgentEndpoints.CANCEL_AGENT(),
      method: "POST",
      data: request,
    });
    if (!result.data.success) {
      throw new Error("Failed to cancel agent");
    }
  }

  /**
   * スレッドのエージェント実行一覧を取得
   */
  static async getAgentRuns(threadId: string, status?: string): Promise<AgentRun[]> {
    try {
      const response = await ApiService.fetchData<undefined, GetAgentRunsResponse>({
        url: AgentEndpoints.GET_AGENT_RUNS(threadId, status),
        method: "GET",
      });

      return response.data.agent_runs.map(buildAgentRun);
    } catch (error) {
      console.error("Failed to fetch agent runs:", error);
      throw error;
    }
  }

  /**
   * サンドボックスからファイルをダウンロード
   */
  static async downloadSandboxFile(filePath: string): Promise<void> {
    try {
      const response = await ApiService.fetchData<undefined, ArrayBuffer>({
        url: AgentEndpoints.DOWNLOAD_SANDBOX_FILE(filePath),
        method: "GET",
        responseType: "blob",
      });

      // ファイル名を抽出
      const filename = filePath.split("/").pop() || "download";

      // Blobを作成してダウンロード
      const blob = new Blob([response.data], { type: "application/octet-stream" });
      const url = globalThis.URL.createObjectURL(blob);

      // ダウンロードリンクを作成してクリック
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // クリーンアップ
      link.remove();
      globalThis.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download file:", error);
      throw error;
    }
  }

  /**
   * サンドボックスにファイルをアップロード
   */
  static async uploadSandboxFile(file: File, targetPath?: string): Promise<UploadSandboxFileResponse> {
    try {
      const formData = new FormData();
      formData.append("file", file);
      if (targetPath) {
        formData.append("target_path", targetPath);
      }

      const response = await ApiService.fetchData<FormData, UploadSandboxFileResponse>({
        url: AgentEndpoints.UPLOAD_SANDBOX_FILE(),
        method: "POST",
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      return response.data;
    } catch (error) {
      console.error("Failed to upload file:", error);
      throw error;
    }
  }

  /**
   * スレッドの総コストを取得
   */
  static async getThreadCosts(threadId: string): Promise<ThreadCostsDto> {
    try {
      const response = await ApiService.fetchData<undefined, ThreadCostsDto>({
        url: AgentEndpoints.GET_THREAD_COSTS(threadId),
        method: "GET",
      });

      return response.data;
    } catch (error) {
      console.error("Failed to fetch thread costs:", error);
      throw error;
    }
  }

  /**
   * コマンド一覧を取得
   */
  static async getCommands(workspaceId: string): Promise<Command[]> {
    try {
      const response = await ApiService.fetchData<undefined, CommandsResponse>({
        url: AgentEndpoints.GET_COMMANDS(workspaceId),
        method: "GET",
      });

      return response.data.commands.map(buildCommand);
    } catch (error) {
      console.error("Failed to fetch commands:", error);
      throw error;
    }
  }

  /**
   * サンドボックスファイル一覧を取得
   */
  static async listSandboxFiles(basePath: string): Promise<string[]> {
    try {
      const response = await ApiService.fetchData<undefined, ListSandboxFilesResponse>({
        url: AgentEndpoints.LIST_SANDBOX_FILES(basePath),
        method: "GET",
      });

      return response.data.files;
    } catch (error) {
      console.error("Failed to list sandbox files:", error);
      throw error;
    }
  }

  /**
   * サンドボックスファイルを削除
   */
  static async deleteSandboxFile(filePath: string): Promise<void> {
    try {
      const response = await ApiService.fetchData<undefined, DeleteSandboxFileResponse>({
        url: AgentEndpoints.DELETE_SANDBOX_FILE(filePath),
        method: "DELETE",
      });
      if (!response.data.success) {
        throw new Error("Failed to delete sandbox file");
      }
    } catch (error) {
      console.error("Failed to delete sandbox file:", error);
      throw error;
    }
  }

  /**
   * コマンド詳細を取得
   */
  static async getCommand(commandId: string): Promise<CommandDetail> {
    try {
      const response = await ApiService.fetchData<undefined, CommandDetailSchema>({
        url: AgentEndpoints.GET_COMMAND(commandId),
        method: "GET",
      });

      return buildCommandDetail(response.data);
    } catch (error) {
      console.error("Failed to fetch command:", error);
      throw error;
    }
  }

  /**
   * コマンドを作成
   */
  static async createCommand(request: CreateCommandRequest): Promise<CommandDetail> {
    try {
      const response = await ApiService.fetchData<CreateCommandRequest, CommandDetailSchema>({
        url: AgentEndpoints.CREATE_COMMAND(),
        method: "POST",
        data: request,
      });

      return buildCommandDetail(response.data);
    } catch (error) {
      console.error("Failed to create command:", error);
      throw error;
    }
  }

  /**
   * コマンドを更新
   */
  static async updateCommand(commandId: string, request: UpdateCommandRequest): Promise<CommandDetail> {
    try {
      const response = await ApiService.fetchData<UpdateCommandRequest, CommandDetailSchema>({
        url: AgentEndpoints.UPDATE_COMMAND(commandId),
        method: "PUT",
        data: request,
      });

      return buildCommandDetail(response.data);
    } catch (error) {
      console.error("Failed to update command:", error);
      throw error;
    }
  }

  /**
   * コマンドを削除
   */
  static async deleteCommand(commandId: string): Promise<void> {
    try {
      const response = await ApiService.fetchData<undefined, DeleteCommandResponse>({
        url: AgentEndpoints.DELETE_COMMAND(commandId),
        method: "DELETE",
      });
      if (!response.data.success) {
        throw new Error("Failed to delete command");
      }
    } catch (error) {
      console.error("Failed to delete command:", error);
      throw error;
    }
  }

  /**
   * サブエージェント一覧を取得
   */
  static async getSubAgents(workspaceId: string): Promise<SubAgent[]> {
    try {
      const response = await ApiService.fetchData<undefined, SubAgentsResponse>({
        url: AgentEndpoints.GET_SUB_AGENTS(workspaceId),
        method: "GET",
      });

      return response.data.subAgents.map(buildSubAgentFromInfo);
    } catch (error) {
      console.error("Failed to fetch sub agents:", error);
      throw error;
    }
  }

  /**
   * サブエージェント詳細を取得
   */
  static async getSubAgent(subAgentId: string): Promise<SubAgent> {
    try {
      const response = await ApiService.fetchData<undefined, SubAgentDetailSchema>({
        url: AgentEndpoints.GET_SUB_AGENT(subAgentId),
        method: "GET",
      });

      return buildSubAgent(response.data);
    } catch (error) {
      console.error("Failed to fetch sub agent:", error);
      throw error;
    }
  }

  /**
   * サブエージェントを作成
   */
  static async createSubAgent(request: CreateSubAgentRequest): Promise<SubAgent> {
    try {
      const response = await ApiService.fetchData<CreateSubAgentRequest, SubAgentDetailSchema>({
        url: AgentEndpoints.CREATE_SUB_AGENT(),
        method: "POST",
        data: request,
      });

      return buildSubAgent(response.data);
    } catch (error) {
      console.error("Failed to create sub agent:", error);
      throw error;
    }
  }

  /**
   * サブエージェントを更新
   */
  static async updateSubAgent(subAgentId: string, request: UpdateSubAgentRequest): Promise<SubAgent> {
    try {
      const response = await ApiService.fetchData<UpdateSubAgentRequest, SubAgentDetailSchema>({
        url: AgentEndpoints.UPDATE_SUB_AGENT(subAgentId),
        method: "PUT",
        data: request,
      });

      return buildSubAgent(response.data);
    } catch (error) {
      console.error("Failed to update sub agent:", error);
      throw error;
    }
  }

  /**
   * サブエージェントを削除
   */
  static async deleteSubAgent(subAgentId: string): Promise<void> {
    try {
      const response = await ApiService.fetchData<undefined, DeleteSubAgentResponse>({
        url: AgentEndpoints.DELETE_SUB_AGENT(subAgentId),
        method: "DELETE",
      });
      if (!response.data.success) {
        throw new Error("Failed to delete sub agent");
      }
    } catch (error) {
      console.error("Failed to delete sub agent:", error);
      throw error;
    }
  }
}

/**
 * APIレスポンスからThreadモデルを構築
 */
const buildThread = (threadSchema: ThreadSchema): Thread => {
  return new Thread(
    threadSchema.id,
    threadSchema.workspace_id,
    threadSchema.title,
    new Date(threadSchema.created_at),
    new Date(threadSchema.updated_at),
  );
};

/**
 * APIレスポンスからCommandモデルを構築
 */
const buildCommand = (commandInfo: CommandInfo): Command => {
  return new Command(
    commandInfo.id,
    commandInfo.name,
    commandInfo.description,
    commandInfo.enabled,
    commandInfo.workspace_id,
    commandInfo.user_id,
    commandInfo.created_at,
  );
};

/**
 * APIレスポンスからMessageモデルを構築
 */
export const buildMessage = (messageSchema: MessageSchema): AgentMessage => {
  return new AgentMessage(
    messageSchema.id,
    messageSchema.threadId,
    messageSchema.role,
    messageSchema.content,
    new Date(messageSchema.created_at),
    messageSchema.metadata,
  );
};

/**
 * APIレスポンスからAgentRunモデルを構築
 */
const buildAgentRun = (agentRunResponse: AgentRunResponse): AgentRun => {
  return new AgentRun(
    agentRunResponse.id,
    agentRunResponse.thread_id,
    agentRunResponse.status,
    agentRunResponse.error_message,
    new Date(agentRunResponse.started_at),
    agentRunResponse.completed_at ? new Date(agentRunResponse.completed_at) : null,
  );
};

/**
 * APIレスポンスからCommandDetailモデルを構築
 */
const buildCommandDetail = (schema: CommandDetailSchema): CommandDetail => {
  return new CommandDetail(
    schema.id,
    schema.name,
    schema.description,
    schema.instruction,
    schema.enabled,
    schema.workspace_id,
    schema.user_id,
    schema.created_at,
    schema.updated_at,
  );
};

/**
 * APIレスポンスからSubAgentモデルを構築
 */
const buildSubAgent = (schema: SubAgentDetailSchema): SubAgent => {
  return new SubAgent(
    schema.id,
    schema.name,
    schema.description,
    schema.instruction,
    schema.tools,
    schema.enabled,
    schema.workspace_id,
    schema.user_id,
    schema.created_at,
    schema.updated_at,
  );
};

/**
 * SubAgentInfoからSubAgentモデルを構築（一覧用）
 */
const buildSubAgentFromInfo = (info: SubAgentInfo): SubAgent => {
  return new SubAgent(
    info.id,
    info.name,
    info.description,
    "", // instruction is not available in list response
    [], // tools is not available in list response
    info.enabled,
    info.workspace_id,
    info.user_id,
    info.created_at,
    info.created_at, // updated_at is not available in list response
  );
};
